import {Controller} from "@hotwired/stimulus";
import {loadStripe} from "@stripe/stripe-js";
import {get, patch} from "@rails/request.js";

export default class extends Controller {
  static targets = ["form", "firstName", "lastName", "email", "confirmEmail", "emailError", "firstNameError", "lastNameError", "submitButton",
    "paymentMessage", "paymentElement", "reserveBtn", 'displayBtn', "marketingOptIn"];

  static values = {
    stripePublishableKey: String,
    clientSecret: String,
    connectedAccountId: String,
    orderSlug: String,
    walletBillingError: String,
    paymentCouldNotComplete: String,
  };

  connect() {
    this.formValid = false;
    // Validate the initial render: server-prefilled fields (logged-in checkout)
    // never fire input events, so without this the button stays in its
    // disabled state even though the form is already complete.
    this.validateForm();
    if (this.hasStripePublishableKeyValue && this.hasClientSecretValue) {
      this.setupStripeElements();
    }

    // Store bound handler reference to ensure proper cleanup in disconnect()
    // bind() creates a new function each time, so we need the same reference for removeEventListener
    this.boundUnloadHandler = this.releaseOrderOnUnload.bind(this);
    window.addEventListener("beforeunload", this.boundUnloadHandler);
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.boundUnloadHandler);
  }

  releaseOrderOnUnload(event) {
    // Once payment is in flight (or done), the hold must NOT be released — doing so
    // would zero the ticket reserve and the paid callback would fail with
    // "reserve(0) < quantity". The model also guards this (release! only runs for a
    // pending order), but skipping the beacon avoids a needless mid-payment request.
    if (this.paymentInProgress) return;

    closeAllModals()
    const url = `/orders/${this.orderSlugValue}/release?slug=${this.orderSlugValue}`;
    navigator.sendBeacon(url);
  }

  async setupStripeElements() {
    try {
      const publishableKey = this.stripePublishableKeyValue;
      const clientSecret = this.clientSecretValue;
      const connectedAccountId = this.connectedAccountIdValue; // pass this in from the server

      if (!publishableKey || !clientSecret || !connectedAccountId) {
        throw new Error("Missing Stripe configuration");
      }

      this.stripe = await loadStripe(publishableKey, {
        stripeAccount: connectedAccountId, // This tells Stripe Elements which account the PaymentIntent belongs to
      });

      this.elements = this.stripe.elements({
        clientSecret,
        appearance: {
          theme: "flat",
          variables: {
            colorText: "#32325d",
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          },
        },
      });

      await this.setupExpressCheckout();

      // Disable Stripe Link: its collapsed "Pay with Link" upsell hides the raw
      // card fields behind an accordion (and pushes them off-screen), which is
      // confusing at checkout and brittle to automate. Show the card form
      // directly. Apple/Google Pay still surface via the Express Checkout
      // element above.
      const paymentElement = this.elements.create("payment", {
        wallets: { link: "never", applePay: "never", googlePay: "never" },
      });
      paymentElement.mount(this.paymentElementTarget);
    } catch (error) {
      console.error("Stripe setup error:", error);
      this.showError("Failed to initialize payment form. Please refresh the page.");
    }
  }

  async setupExpressCheckout() {
    const expressElement = this.elements.create("expressCheckout", {
      buttonType: {
        applePay: "buy",
        googlePay: "buy",
      },
      layout: { overflow: "never" },
    });
  
    const container = document.getElementById("express-checkout-element");
    if (!container) return;
  
    expressElement.mount(container);
  
    // Hide wrapper if neither Apple Pay nor Google Pay is available
    expressElement.on("ready", ({ availablePaymentMethods }) => {
      const wrapper = document.getElementById("express-checkout-wrapper");
      if (!wrapper) return;
      if (!availablePaymentMethods || Object.keys(availablePaymentMethods).length === 0) {
        wrapper.classList.add("d-none");
      }
    });
  
    expressElement.on("confirm", async (event) => {
      // Validate your billing fields first
      const isEmailsValid = this.validateEmails();
      const areFieldsFilled = this.validateRequiredFields();
      const areNamesValid = this.validateNames();

      if (!isEmailsValid || !areFieldsFilled || !areNamesValid) {
        event.paymentFailed({
          reason: "fail",
          message: this.walletBillingErrorValue,
        });
        this.focusBlankTarget();
        return;
      }

      // Inline order-level custom questions must be answered before the wallet
      // charges the buyer — the server rejects a blank required answer with a
      // 422, so catch it here instead of failing the payment sheet.
      if (!this.reportFormValidity()) {
        event.paymentFailed({
          reason: "fail",
          message: this.walletBillingErrorValue,
        });
        return;
      }

      this.paymentInProgress = true;
      try {
        const orderResponse = await this.updateOrder();
        if (!orderResponse.ok) {
          const message = await this.orderUpdateErrorMessage(orderResponse);
          event.paymentFailed({ reason: "fail", message });
          this.showError(message);
          this.paymentInProgress = false;
          return;
        }

        const { error, paymentIntent } = await this.stripe.confirmPayment({
          elements: this.elements,
          confirmParams: {
            return_url: this.paymentReturnUrl(),
          },
          redirect: "if_required",
        });

        if (error) {
          event.paymentFailed({ reason: "fail", message: error.message });
          this.showError(error.message);
          this.paymentInProgress = false;
          return;
        }

        await this.confirmationRequest(paymentIntent?.id);
      } catch (err) {
        const message =
          err?.message || this.paymentCouldNotCompleteValue;
        event.paymentFailed({ reason: "fail", message });
        this.showError(message);
        this.paymentInProgress = false;
      }
    });
  }

  paymentReturnUrl() {
    const url = new URL(window.location.href);
    url.hash = "";
    return url.toString();
  }

  async orderUpdateErrorMessage(response) {
    try {
      const data = await response.json;
      if (data?.errors) return data.errors;
      if (data?.error) return data.error;
    } catch (_) {
      // non-JSON error body
    }
    return "Failed to update order information";
  }

  validateForm() {
    const isEmailsValid = this.validateEmails();
    const areFieldsFilled = this.validateRequiredFields();
    const areNamesValid = this.validateNames();

    this.formValid = isEmailsValid && areFieldsFilled && areNamesValid;
    this.displayBtnTarget.classList.toggle('general-disable-btn', !this.formValid)
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !this.formValid;
    }
  }

  // The <form> this controller drives. The PAID form (payments/_new) declares a
  // "form" target, but the FREE/zero-total form (payments/_user_info_form) does
  // NOT — and referencing a missing Stimulus target THROWS, which silently killed
  // reserveOrder() (Reserve button did nothing). In both views the controller is
  // declared on the <form> itself, so this.element is a safe fallback.
  get formElement() {
    if (this.hasFormTarget) return this.formTarget;
    return this.element.tagName === "FORM" ? this.element : this.element.closest("form");
  }

  reserveOrder() {
    const isEmailsValid = this.validateEmails();
    const areFieldsFilled = this.validateRequiredFields();
    const areNamesValid = this.validateNames();

    if (!(isEmailsValid && areFieldsFilled && areNamesValid)) {
      this.focusBlankTarget()
      return;
    }

    // The inline order-level custom questions live in this SAME form and are not
    // covered by the contact-field checks above. Let constraint validation run
    // BEFORE we paint the button busy: clicking the hidden submit button on an
    // invalid form fires no submit event, so the loader would otherwise spin
    // forever and the buyer could never retry.
    if (!this.reportFormValidity()) return;

    // Add loading state to the Reserve button
    this.displayBtnTarget.classList.add('loading');

    if (this.hasReserveBtnTarget) {
      this.reserveBtnTarget.click()
    } else {
      if (this.hasSubmitButtonTarget) this.submitButtonTarget.click()
    }
  }

  // True when the browser's own constraint validation passes for the whole form
  // (contact fields AND the inline custom questions). On failure we surface the
  // offender ourselves as well — a clip-hidden control (the styled file-upload
  // input) can't show a native bubble, which would otherwise leave the buyer
  // staring at a form that silently refuses to submit.
  reportFormValidity() {
    const form = this.formElement;
    // No form found / no constraint API: never block the buyer — the server-side
    // required-question gate still protects the data.
    if (!form || typeof form.checkValidity !== "function" || form.checkValidity()) return true;

    const invalid = form.querySelector("input:invalid, select:invalid, textarea:invalid");
    if (invalid) {
      const field = invalid.closest(".question-field, .input-wrapper, .fu-field") || invalid;
      field.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof invalid.reportValidity === "function") invalid.reportValidity();
      if (typeof invalid.focus === "function") invalid.focus({ preventScroll: true });
    }
    return false;
  }

  focusBlankTarget() {
    let targets = [this.firstNameTarget, this.lastNameTarget, this.emailTarget, this.confirmEmailTarget]
    for (const target of targets) {
      if (target.value == '') {
        target.focus()
        return
      }
    }
  }

  validateEmails() {
    const email = this.emailTarget.value.trim();
    const confirmEmail = this.confirmEmailTarget.value.trim();

    if (email && confirmEmail) {
      if (email !== confirmEmail) {
        this.emailErrorTarget.classList.remove('d-none')
        return false;
      }
      this.emailErrorTarget.classList.add('d-none')
      return true;
    }
    return false;
  }

  validateRequiredFields() {
    return [this.firstNameTarget, this.lastNameTarget, this.emailTarget, this.confirmEmailTarget].every((field) => field.value.trim() !== "");
  }

  validateNames() {
    const namePattern = /^[a-zA-Z\s\-']+$/;
    const firstName = this.firstNameTarget.value.trim();
    const lastName = this.lastNameTarget.value.trim();
    
    let isValid = true;

    // Validate first name
    if (firstName && !namePattern.test(firstName)) {
      if (this.hasFirstNameErrorTarget) {
        this.firstNameErrorTarget.classList.remove('d-none');
      }
      isValid = false;
    } else {
      if (this.hasFirstNameErrorTarget) {
        this.firstNameErrorTarget.classList.add('d-none');
      }
    }

    // Validate last name
    if (lastName && !namePattern.test(lastName)) {
      if (this.hasLastNameErrorTarget) {
        this.lastNameErrorTarget.classList.remove('d-none');
      }
      isValid = false;
    } else {
      if (this.hasLastNameErrorTarget) {
        this.lastNameErrorTarget.classList.add('d-none');
      }
    }

    return isValid;
  }

  showError(message) {
    this.paymentMessageTarget.textContent = message;
    this.paymentMessageTarget.classList.remove("d-none");
    this.paymentMessageTarget.style.display = "block";
    // Remove loading state from Reserve button on error
    if (this.hasDisplayBtnTarget) {
      this.displayBtnTarget.classList.remove('loading');
    }
  }

  async submit(event) {
    event.preventDefault();

    // Bail-outs must UNDO the busy state reserveOrder() painted on the pay
    // button, otherwise the spinner sticks and the button is dead for good.
    if (!this.formValid) {
      this.clearBusyState();
      return;
    }
    this.paymentInProgress = true;
    this.submitButtonTarget.disabled = true;
    const spinner = document.getElementById("spinner");
    if (spinner) spinner.classList.remove("d-none");

    try {
      const orderResponse = await this.updateOrder();
      if (!orderResponse.ok) {
        const message = await this.orderUpdateErrorMessage(orderResponse);
        throw new Error(message);
      }

      // Order-level FILE custom answers can't ride the JSON PATCH above (and
      // collectOrderQuestions skips file inputs), so upload them via separate
      // multipart requests now — BEFORE confirming payment, so a wrong-type /
      // oversize file is surfaced as an error and the buyer isn't charged for a
      // submission that drops their file. Throws on the first failure.
      await this.uploadOrderQuestionFiles();

      const {error, paymentIntent} = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          return_url: this.paymentReturnUrl(),
        },
        redirect: "if_required",
      });

      if (error) {
        throw error;
      } else {
        await this.confirmationRequest(paymentIntent?.id);
      }
    } catch (error) {
      console.error("Payment error:", error);
      this.showError(error.message || "An unexpected error occurred");
      this.submitButtonTarget.disabled = false;
      this.clearBusyState();
      this.paymentInProgress = false;
    }
  }

  // Return the pay/reserve button to its idle, clickable state.
  clearBusyState() {
    const spinner = document.getElementById("spinner");
    if (spinner) spinner.classList.add("d-none");
    if (this.hasDisplayBtnTarget) this.displayBtnTarget.classList.remove('loading');
  }

  async updateOrder() {
    const body = {
      first_name: this.firstNameTarget.value,
      last_name: this.lastNameTarget.value,
      email_address: this.emailTarget.value,
      // Marketing consent: read the checkbox so the buyer's choice reaches
      // the order (and the downstream attendee contact). Without this the
      // PATCH omits the field and the order keeps its FALSE default, silently
      // dropping consent the buyer actually gave on the form.
      marketing_opt_in: this.hasMarketingOptInTarget && this.marketingOptInTarget.checked ? "1" : "0",
    };

    // Order-level custom questions (non-personalized events render them inline on
    // this step). The JSON PATCH would otherwise omit them, silently dropping the
    // answers AND tripping the server-side required gate on a paid order. Collect
    // any order[questions][...] fields from the form and send them as
    // body.questions. NOTE: ParamsWrapper folds only real Order attributes into
    // params[:order], so these land at the TOP level server-side —
    // OrdersController#submitted_order_questions reads both nestings.
    const questions = this.collectOrderQuestions();
    if (Object.keys(questions).length > 0) body.questions = questions;

    return patch(this.formElement.action, {
      contentType: "application/json",
      responseKind: "json",
      body,
    });
  }

  // Build { questionId: value | [values] } from the inline order-question inputs.
  // Mirrors Rails param nesting for order[questions][ID] and order[questions][ID][]
  // (checkbox groups). Unchecked checkboxes/radios contribute nothing.
  collectOrderQuestions() {
    const result = {};
    const fields = this.formElement.querySelectorAll('[name^="order[questions]"]');
    fields.forEach((field) => {
      const match = field.name.match(/^order\[questions\]\[(\d+)\](\[\])?$/);
      if (!match) return;
      const id = match[1];
      const isArray = Boolean(match[2]);
      const type = (field.type || "").toLowerCase();

      // Skip file inputs: a file's .value is the fake "C:\fakepath\name.png"
      // string, never the bytes, and a JSON body can't carry a file anyway.
      // Sending it would write that garbage string as the answer. The actual
      // file upload on the paid (Stripe JSON) path is a separate fix (pending).
      if (type === "file") return;

      if ((type === "checkbox" || type === "radio") && !field.checked) return;

      if (isArray) {
        (result[id] ||= []).push(field.value);
      } else {
        result[id] = field.value;
      }
    });
    return result;
  }

  // Upload each selected order-level FILE question via a dedicated multipart
  // request (orders#upload_question_file). The JSON order PATCH can't carry a
  // file, so these post separately. Uses @rails/request.js patch with a FormData
  // body (browser sets multipart; request.js still attaches the CSRF token).
  // Throws on the first server rejection (wrong type / oversize) so submit()
  // surfaces it and halts BEFORE payment is confirmed.
  async uploadOrderQuestionFiles() {
    const fileInputs = this.formElement.querySelectorAll('input[type="file"][name^="order[questions]"]');
    for (const input of fileInputs) {
      const match = input.name.match(/^order\[questions\]\[(\d+)\]$/);
      if (!match) continue;
      const file = input.files && input.files[0];
      if (!file) continue; // optional question left empty — nothing to upload

      const body = new FormData();
      body.append("question_id", match[1]);
      body.append("file", file);

      const url = `/orders/${this.orderSlugValue}/upload_question_file?slug=${this.orderSlugValue}`;
      const response = await patch(url, { body, responseKind: "json" });
      if (!response.ok) {
        let message = this.paymentCouldNotCompleteValue || "We couldn't upload your file.";
        try {
          const data = await response.json;
          if (data && data.errors) message = data.errors;
        } catch (_) { /* keep fallback */ }
        throw new Error(message);
      }
    }
  }

  async confirmationRequest(paymentIntentId) {
    // Payment has already succeeded by this point; this fetches the confirmation /
    // registration step. If it fails (e.g. a server 500), surface it and stop the
    // spinner instead of leaving the form hanging.
    // The payment_intent_id lets the server settle the order by a direct (read-
    // after-write-safe) retrieve instead of Stripe's eventually-consistent
    // search, which often misses a PaymentIntent created seconds earlier.
    const params = new URLSearchParams({ order_slug: this.orderSlugValue });
    if (paymentIntentId) params.set("payment_intent_id", paymentIntentId);
    const response = await get(`/payments/confirmation?${params}`, {
      contentType: "application/json",
      responseKind: "turbo-stream",
    });
    if (!response.ok) {
      throw new Error(this.paymentCouldNotCompleteValue || "We couldn't load your confirmation. Your payment went through — please refresh or check your email.");
    }
  }
}
